import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { LibraryReviewHeader } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ContentComparisonCard';
import { VersionComparisonCard } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/VersionComparisonCard';
import { ContentGenComparisonView } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/ContentGenComparisonView';
import { useKeywordsDisplayStore, useEditingFieldsStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { useProjectReview } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useProjectReview';
import { useEditGeneratedContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useEditGeneratedContent';
import {
  ContentFormSchema,
  ContentForm,
  sectionsToFormValues
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/types/contentFormTypes';
import { ActionBarProps } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/LibraryReviewActionBar';

const ProjectFinalReview: React.FC = () => {
  const { setShowKeywords } = useKeywordsDisplayStore();
  const { clearAllEditing, editingFields } = useEditingFieldsStore();

  useEffect(() => {
    setShowKeywords(true);
  }, [setShowKeywords]);

  // Clear all editing states when component unmounts
  useEffect(() => {
    return () => {
      clearAllEditing();
    };
  }, [clearAllEditing]);

  const {
    leftMetrics,
    rightMetrics,
    metricLoadingStates,
    currentContentPhrasesKeywords,
    generatedContentPhrasesKeywords,
    isLoading,
    isLoadingLeft,
    isLoadingRight,
    isLoadingLibrary,
    error,
    actionBarProps: baseActionBarProps,
    isExporting,
    handleExport,
    leftVersionDetails,
    rightVersionDetails,
    currentContentGenId,
    projectReviewQueryKey,
    // Version selection
    availableVersions,
    leftComparisonOptions,
    leftSelectedVersion,
    rightSelectedVersion,
    leftVersionName,
    rightVersionName,
    handleLeftVersionChange,
    handleRightVersionChange,
    // Syndication data
    retailerId,
    retailerSku
  } = useProjectReview();

  const { isPending: isEditingContent } = useEditGeneratedContent();

  // Initialize form with content data
  const formDefaultValues = useMemo<ContentForm>(() => {
    const currentContent = sectionsToFormValues(currentContentPhrasesKeywords);
    const generatedContent = sectionsToFormValues(generatedContentPhrasesKeywords);

    return {
      currentContent: {
        title: currentContent.title || '',
        itemHighlights: currentContent.itemHighlights || '',
        bullets: currentContent.bullets || [],
        description: currentContent.description || ''
      },
      generatedContent: {
        title: generatedContent.title || '',
        itemHighlights: generatedContent.itemHighlights || '',
        bullets: generatedContent.bullets || [],
        description: generatedContent.description || ''
      }
    };
  }, [currentContentPhrasesKeywords, generatedContentPhrasesKeywords]);

  const methods = useForm<ContentForm>({
    mode: 'onChange',
    resolver: zodResolver(ContentFormSchema),
    defaultValues: formDefaultValues
  });

  // Update form when content changes, but only if no fields are currently being edited
  useEffect(() => {
    if (!isLoading && editingFields.size === 0) {
      methods.reset(formDefaultValues);
    }
  }, [formDefaultValues, isLoading, methods, editingFields]);

  // Inline editing already saves changes via EditableContentCard, so just use baseActionBarProps directly
  const actionBarProps: ActionBarProps = {
    ...baseActionBarProps,
    isSaving: baseActionBarProps.isSaving || isEditingContent
  };

  // Rendering is controlled by useProjectReview's redirect logic

  if (error) {
    return (
      <BasePageContainer>
        <div>Error loading content.</div>
      </BasePageContainer>
    );
  }

  return (
    <FormProvider {...methods}>
      <BasePageContainer>
        <LibraryReviewHeader
          handleExport={handleExport}
          isExporting={isExporting}
          retailerId={retailerId}
          retailerSku={retailerSku}
          contentGenId={currentContentGenId}
        />
        <Flex gap={41} sx={{ mb: 4 }}>
          <VersionComparisonCard
            versionName={leftVersionName}
            details={leftVersionDetails}
            detailsLoading={isLoadingLeft}
            detailsVariant={leftSelectedVersion === 'current' ? 'current' : leftSelectedVersion === 'master' ? 'master' : 'generated'}
            metrics={{
              contentUtilization: leftMetrics.contentUtilization,
              keywordsUsed: leftMetrics.keywordsUsed as number,
              productiveKeywords: leftMetrics.productiveKeywords as number,
              keywordPopularity: leftMetrics.keywordPopularity
            }}
            isLoading={isLoadingLeft}
            isVersionsLoading={isLoadingLibrary}
            versions={leftComparisonOptions}
            onVersionChange={handleLeftVersionChange}
            metricLoadingStates={{
              utilization: metricLoadingStates.leftUtilization || isLoadingLeft || isLoadingRight,
              keywordUsage: metricLoadingStates.leftKeywordUsage || isLoadingLeft || isLoadingRight,
              productivity: metricLoadingStates.leftKeywordProductivity || isLoadingLeft || isLoadingRight,
              popularity: metricLoadingStates.leftKeywordPopularity || isLoadingLeft || isLoadingRight
            }}
            showSetAsMaster={false}
          />
          <VersionComparisonCard
            versionName={rightVersionName}
            details={rightVersionDetails}
            detailsLoading={isLoadingRight}
            detailsVariant={rightSelectedVersion === 'current' ? 'current' : rightSelectedVersion === 'master' ? 'master' : 'generated'}
            metrics={{
              contentUtilization: rightMetrics.contentUtilization,
              keywordsUsed: rightMetrics.keywordsUsed as number,
              productiveKeywords: rightMetrics.productiveKeywords as number,
              keywordPopularity: rightMetrics.keywordPopularity
            }}
            changes={rightMetrics.changes}
            isLoading={isLoadingRight}
            isVersionsLoading={isLoadingLibrary}
            versions={availableVersions}
            onVersionChange={handleRightVersionChange}
            metricLoadingStates={{
              utilization: metricLoadingStates.rightUtilization || isLoadingLeft || isLoadingRight,
              keywordUsage: metricLoadingStates.rightKeywordUsage || isLoadingLeft || isLoadingRight,
              productivity: metricLoadingStates.rightKeywordProductivity || isLoadingLeft || isLoadingRight,
              popularity: metricLoadingStates.rightKeywordPopularity || isLoadingLeft || isLoadingRight
            }}
            showSetAsMaster={false}
          />
        </Flex>
        <ContentGenComparisonView
          currentContentPhrasesKeywords={currentContentPhrasesKeywords}
          generatedContentPhrasesKeywords={generatedContentPhrasesKeywords}
          actionBarProps={actionBarProps}
          queryKey={projectReviewQueryKey}
          leftLoading={isLoadingLeft}
          rightLoading={isLoadingRight}
        />
      </BasePageContainer>
    </FormProvider>
  );
};

export default ProjectFinalReview;